<template>
  <div class="requisition-report py-4 ga-2" data-png-export="requisition-report">
    <RequisitionReportHeader class="full" />
    <RequisitionReportPosts class="two-thirds" />
    <RequisitionReportTeam class="one-third" @requisitions-tracking-event="handleTrackingEvent" />
    <RequisitionReportApplicantsWidget class="one-third chart-row-item" :project-name="projectName" />
    <RequisitionReportGenderChart class="one-third chart-row-item" :project-name="projectName" />
    <RequisitionReportSourceChart class="one-third chart-row-item" :project-name="projectName" />
    <RequisitionReportTable
      :candidate-stage-options="candidateStageOptions"
      class="full"
      :selected-stage="exportInProgress ? '' : selectedCandidateStage"
      :project-name="projectName"
      :downloading="downloadingCSV"
      @set-candidate-stage="(stage) => handleSetCandidateStage(stage, true)"
      @download-csv="downloadApplicationsTable"
    />
    <RequisitionReportActiveCandidatesTable
      v-if="!exportInProgress"
      :candidate-stage-options="candidateStageOptions"
      class="full"
      :selected-stage="selectedCandidateStage"
      :project-name="projectName"
      @set-candidate-stage="handleSetCandidateStage"
    />
  </div>
</template>

<script setup>
import {
  computed, watch, nextTick, onMounted, onBeforeUnmount, ref,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useGoTo } from 'vuetify';
import { useHead } from '@unhead/vue';
import { storeToRefs } from 'pinia';
import { startCase } from 'lodash-es';

import { useRequisitionReport } from '@/stores/requisitions/requisitionReport';
import { useAppAuthData } from '@/stores/app/authData';
import { useTracking } from '@/stores/tracking/tracking';
import { useTooltips } from '@/stores/insights/tooltips';
import { useInsightsExport } from '@/stores/insights/export';
import { useInsightsShare } from '@/stores/insights/share';

import RequisitionReportHeader from '@/components/requisitions/RequisitionReportHeader.vue';
import RequisitionReportPosts from '@/components/requisitions/RequisitionReportPosts.vue';
import RequisitionReportTeam from '@/components/requisitions/RequisitionReportTeam.vue';
import RequisitionReportApplicantsWidget from '@/components/requisitions/RequisitionReportApplicantsWidget/RequisitionReportApplicantsWidget.vue';
import RequisitionReportGenderChart from '@/components/requisitions/RequisitionReportGenderChart.vue';
import RequisitionReportSourceChart from '@/components/requisitions/RequisitionReportSourceChart.vue';
import RequisitionReportTable from '@/components/requisitions/RequisitionReportTable.vue';
import RequisitionReportActiveCandidatesTable from '@/components/requisitions/RequisitionReportActiveCandidatesTable.vue';

// Stores
const {
  title,
  status,
  table,
  candidatesLoading,
  applicantWidgetUnit,
  tableData,
  shareHiringTeam,
  selectedCandidateStage,
  candidatesSort,
} = storeToRefs(useRequisitionReport());

const {
  getRequisitionReport,
  getShareUsersWithAccess,
  updateReqUuid,
  getActiveCandidatesByStage,
  updateCandidatesSorting,
  resetReqReportData,
  downloadApplicantCountCSV,
  downloadActiveCandidatesCSV,
  updateActiveCandidatesStage,
  updateViaQueryParams,
} = useRequisitionReport();

const { loadTooltips } = useTooltips();

const { authData } = storeToRefs(useAppAuthData());

const { exportInProgress } = storeToRefs(useInsightsExport());
const { setExportConfig, resetExportConfig } = useInsightsExport();

const { setShareConfig, resetShareConfig } = useInsightsShare();

const goTo = useGoTo();
const route = useRoute();
const router = useRouter();

const dontScrollToCandidatesTable = ref(false);
const downloadingCSV = ref(false);

const tooltipKeys = [
  'single-req-table',
  'single-req-source',
  'single-req-gender',
  'single-req-apps',
  'single-req-job-posts',
  'req-report-open-days-open',
  'req-report-closed-days-open',
  'req-report-warning-unmapped-cand',
  'req-report-active-candidate-table',
];

const projectName = computed(() => authData.value.projectName);

const hasTitleAndStatus = computed(() => title.value && status.value);

const candidateStageOptions = computed(() => {
  const potentialStages = ['screen', 'assessment', 'offer'];
  const finalOptions = [];

  potentialStages.forEach((stage) => {
    if (table.value[stage]?.active) {
      finalOptions.push(stage);
    }
  });

  return finalOptions;
});

const csvDownloadStageOptions = computed(() => {
  const potentialStages = ['application', 'screen', 'assessment', 'offer'];
  const finalOptions = [];

  potentialStages.forEach((stage) => {
    if (table.value[stage]?.active) {
      finalOptions.push(stage);
    }
  });

  return finalOptions;
});

const shareConfig = computed(() => ({
  shareable: true,
  showReportImageButton: true,
  usersWithAccess: shareHiringTeam.value,
}));

const downloadApplicationsTable = async () => {
  downloadingCSV.value = true;

  await downloadApplicantCountCSV(projectName.value);

  useTracking().insightsDownloadReport({
    reportName: route.name,
    downloadType: 'Individual Widgets',
    downloadLocation: 'Individual Widget',
    fileType: 'CSV',
    widgetName: 'Applicant Count Table',
  });

  downloadingCSV.value = false;
};

const reportExportConfig = computed(() => {
  const stageNames = {
    application: 'Application',
    screen: 'Screening',
    assessment: 'Assessment',
    offer: 'Offer',
  };

  const activeTableData = tableData.value.find(({ label }) => label === 'Active');
  const screeningCount = activeTableData?.screen?.value || 0;
  const assessmentCount = activeTableData?.assessment?.value || 0;
  const offerCount = activeTableData?.offer?.value || 0;
  const applicationCount = activeTableData?.application?.value || 0;
  const totalCount = screeningCount + assessmentCount + offerCount + applicationCount;

  const stageCounts = {
    application: applicationCount,
    screen: screeningCount,
    assessment: assessmentCount,
    offer: offerCount,
  };

  return {
    csvExport: true,
    pngExport: true,
    isReqReport: true,
    pageDownloadId: 'requisition-report',
    pngWidgetOptions: {
      'requisition-applicants': `Recent Applications: ${startCase(applicantWidgetUnit.value)}`,
      'requisition-gender': 'Graph: Gender Distribution of Apps',
      'requisition-source': 'Graph: Source Distribution of Apps',
      'applicants-table': 'Table: Application Data',
    },
    csvOptions: [
      { label: 'Applicant Count Table', csvDownloadFunction: () => downloadApplicantCountCSV(projectName.value) },
      {
        label: 'Active Candidate Table',
        csvDownloadFunction: (selectedStages) => downloadActiveCandidatesCSV(
          selectedStages,
          projectName.value,
        ),
        children: csvDownloadStageOptions.value.map((stage) => ({
          label: `${stageNames[stage] || startCase(stage)} (${stageCounts[stage] || 0})`,
          stage,
        })),
        selectAllLabel: `All stages (${totalCount} candidates)`,
      },
    ],
  };
});

// Head management
const titleValue = computed(() => {
  const titleToUse = route.meta.title({ title: title.value }) || 'Requisition Report';
  const parentTitleToUse = route.meta.parentTitle({ status: status.value }) || '';

  return parentTitleToUse ? `${titleToUse} - ${parentTitleToUse}` : titleToUse;
});

useHead({
  title: () => titleValue.value,
});

// Watchers
watch(() => selectedCandidateStage.value, (newValue) => {
  if (newValue) {
    getActiveCandidatesByStage(newValue);
  } else {
    updateCandidatesSorting();
  }
});

watch(() => hasTitleAndStatus.value, (newValue) => {
  if (newValue) {
    const recentRoutes = JSON.parse(localStorage.getItem('recentViews')) || [];
    const recentRouteIndex = recentRoutes.findIndex((recentRoute) => recentRoute.name === 'requisition-report'
      && recentRoute.params.id === route.params.id);
    const { title: titleFunc, parentTitle: parentTitleFunc } = route.meta;

    if (typeof recentRouteIndex === 'number' && recentRoutes[recentRouteIndex]) {
      recentRoutes[recentRouteIndex].meta.title = titleFunc({ title: title.value });
      recentRoutes[recentRouteIndex].meta.parentTitle = parentTitleFunc({ status: status.value });

      localStorage.setItem('recentViews', JSON.stringify(recentRoutes));
    }
  }
});

watch(() => candidatesLoading.value, (newValue) => {
  if (!newValue && selectedCandidateStage.value && !dontScrollToCandidatesTable.value) {
    dontScrollToCandidatesTable.value = false;

    nextTick(() => {
      goTo(
        document.querySelector('.requisition-report-active-candidates'),
        { easing: 'easeInOutQuad', duration: 500, container: '.datapeople-frame' },
      );
    });
  }
});

watch(() => shareConfig.value, (newValue) => {
  setShareConfig(newValue);
}, { deep: true });

watch(() => reportExportConfig.value, (newConfig) => {
  setExportConfig(newConfig);
}, { deep: true });

watch([
  () => applicantWidgetUnit.value,
  () => selectedCandidateStage.value,
  () => candidatesSort.value,
], ([
  newApplicantWidgetUnit,
  newSelectedCandidateStage,
  newCandidatesSort,
]) => {
  if (newSelectedCandidateStage) {
    router.replace({
      query: {
        ...route.query,
        recent_apps: newApplicantWidgetUnit || undefined,
        active_candidate_stage: newSelectedCandidateStage || undefined,
        active_candidates_sort: newCandidatesSort || undefined,
      },
    });
  } else {
    router.replace({
      query: {
        ...route.query,
        recent_apps: newApplicantWidgetUnit || undefined,
        active_candidate_stage: undefined,
        active_candidates_sort: undefined,
      },
    });
  }
}, { immediate: true });

// Lifecycle
onMounted(() => {
  const { id } = route.params;

  updateReqUuid(id);

  setShareConfig({
    showReportImageButton: true,
    usersWithAccess: shareHiringTeam.value,
  });

  setExportConfig(reportExportConfig.value);

  getRequisitionReport(id);

  getShareUsersWithAccess(id);

  loadTooltips(tooltipKeys);

  updateViaQueryParams(route.query);

  if (route.query?.active_candidate_stage) {
    dontScrollToCandidatesTable.value = true;
  }

  useTracking().requisitionReportLanded(id);
});

onBeforeUnmount(() => {
  resetReqReportData();

  resetShareConfig();
  resetExportConfig();

  updateActiveCandidatesStage(null);
});

// Methods
const handleTrackingEvent = ({ action, args }) => {
  useTracking()[action](args);
};

const handleSetCandidateStage = (stage, shouldScroll = false) => {
  updateActiveCandidatesStage(stage === selectedCandidateStage.value ? null : stage);

  nextTick(() => {
    if (selectedCandidateStage.value && shouldScroll) {
      goTo(
        document.querySelector('.requisition-report-active-candidates'),
        { easing: 'easeInOutQuad', duration: 500, container: '.datapeople-frame' },
      );
    }
  });
};
</script>

<style lang="less" scoped>
@import '@/less/mixins';

.requisition-report {
  display: grid;
  grid-template: 124px 459px 316px auto / repeat(3, minmax(0, 1fr));
  width: 100%;
  min-width: 1288px;
  max-width: 1488px;
  padding: 0px 44px;
  margin: 0 auto;
}

.full {
  grid-column: span 3;
}

.two-thirds {
  grid-column: span 2;
}

.one-third {
  grid-column: span 1;
}

.chart-row-item {
  z-index: 2; // Prevents the candidate table from overlapping
}
</style>
